import { type ReactNode } from 'react'
import cx from '@/utils/classnames'
import { GridList, GridListItem } from 'react-aria-components'
import { Icon, type IconName } from '@/components/Icon'
import { Button } from '@/components/Button'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/components/EmptyState'

import styles from './ServiceTypes.module.css'
import {
  AutoSkeleton,
  SkeletonList,
  useSkeletonListContext,
} from '@/components/Skeleton'
import { useIsSpacious } from '@/hooks/viewport-hooks'

/**
 * The ServiceTypeEmptyState
 */
export function ServiceTypeEmptyState() {
  return (
    <EmptyState>
      <EmptyStateTitle>No service types.</EmptyStateTitle>
      <EmptyStateDescription>
        Once a service type is added it will be displayed here.
      </EmptyStateDescription>
    </EmptyState>
  )
}

interface ServiceTypeListProps {
  isSkeleton?: boolean
  children?: ReactNode
  testId?: string
}

/**
 * The ServiceTypeList
 */
export function ServiceTypeList({
  isSkeleton,
  children,
  testId,
}: ServiceTypeListProps) {
  const isSpacious = useIsSpacious()
  return (
    <GridList
      aria-label="Service types"
      className={cx(styles.root)}
      layout={isSpacious ? 'grid' : 'stack'}
      data-test={testId}
    >
      {isSkeleton ? (
        <>
          <SkeletonList count={4}>
            <ServiceTypeSkeleton />
          </SkeletonList>
        </>
      ) : (
        children
      )}
    </GridList>
  )
}

interface ServiceTypeListItemProps {
  title: string
  description: string
  icon: IconName
  isActive?: boolean
  isDisabled?: boolean
  onStart: () => void
  isPending?: boolean
  testId?: string
  startButtonTestId?: string
  startButtonLabel?: string
}

/**
 * The ServiceTypeListItem
 */
export function ServiceTypeListItem({
  icon,
  title,
  description,
  isActive,
  isDisabled,
  onStart,
  isPending,
  testId,
  startButtonTestId,
  startButtonLabel = 'Start now',
}: ServiceTypeListItemProps) {
  return (
    <GridListItem
      textValue={title}
      isDisabled={isDisabled}
      onAction={onStart}
      data-test={testId}
      className={({ isHovered, isFocusVisible, isDisabled }) =>
        cx(
          styles.serviceType,
          isHovered && styles.hover,
          isFocusVisible && styles.focusVisible,
          isActive && styles.active,
          isDisabled && styles.disabled,
        )
      }
    >
      <div className={styles.serviceTypeHeader}>
        <Icon name={icon} />
        <h2 className={styles.title}>{title}</h2>
      </div>

      <div className={styles.serviceTypeContent}>
        <h2 className={styles.title}>{title}</h2>
        <div className="text-body-md-light">{description}</div>
        <div className={styles.action}>
          <div className="flex-1 flex flex-col">
            <Button
              onPress={onStart}
              variant="default"
              label={startButtonLabel}
              isDisabled={isDisabled}
              isPending={isPending}
              testId={startButtonTestId}
            />
          </div>
          <div className={styles.spacer}></div>
        </div>
      </div>
    </GridListItem>
  )
}

/**
 * The ServiceTypeSkeleton
 */
export function ServiceTypeSkeleton() {
  const { vary } = useSkeletonListContext()

  return (
    <GridListItem textValue="Service Type" className={cx(styles.serviceType)}>
      <div className={styles.serviceTypeHeader}>
        <h2 className={cx(styles.title)}>
          <AutoSkeleton isSkeleton characters={vary(15)}>
            Title
          </AutoSkeleton>
        </h2>
      </div>

      <div className={styles.serviceTypeContent}>
        <h2 className={cx(styles.title)}>
          <AutoSkeleton isSkeleton characters={vary(15)}>
            Title
          </AutoSkeleton>
        </h2>
        <div className="text-body-md-light">
          <AutoSkeleton isSkeleton characters={vary(80)}>
            Description
          </AutoSkeleton>
        </div>

        <div className={styles.action}>
          <div className="flex-1 flex flex-col">
            <Button variant="outline" label="Start now" isSkeleton />
          </div>
          <div className={styles.spacer}></div>
        </div>
      </div>
    </GridListItem>
  )
}
